import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, Check, ArrowLeft, Tag, FileText, List, Hash } from "lucide-react";

const TABS = [
    { id: "amazon", label: "Amazon" },
    { id: "shopify", label: "Shopify" },
    { id: "etsy", label: "Etsy" },
    { id: "ebay", label: "eBay" },
];

function CopyBtn({ text, id }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(typeof text === "string" ? text : JSON.stringify(text, null, 2));
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };
    return (
        <button onClick={copy} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 bg-surface-2 text-xs text-zinc-400 hover:text-accent hover:border-accent/40 transition-colors" data-testid={`copy-${id}`}>
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function Section({ title, icon: Icon, copyId, copyText, children }) {
    return (
        <div className="surface p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="w-3.5 h-3.5 text-zinc-500" />}
                    <h3 className="overline !text-zinc-400">{title}</h3>
                </div>
                {copyText !== undefined && <CopyBtn text={copyText} id={copyId} />}
            </div>
            {children}
        </div>
    );
}

export default function Results() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("amazon");

    useEffect(() => {
        api.get(`/products/${id}`)
            .then(({ data }) => setProduct(data))
            .catch(() => toast.error("Failed to load listing"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="p-12 text-center font-mono text-zinc-500" data-testid="results-loading">Loading...</div>;
    if (!product) return <div className="p-12 text-center text-zinc-500" data-testid="results-not-found">Not found</div>;

    const r = product.result || {};
    const mp = r.marketplaces?.[tab] || {};

    return (
        <div className="max-w-6xl mx-auto px-6 py-12" data-testid="results-page">
            <Link to="/dashboard" className="inline-flex items-center gap-2 mb-6 font-mono text-xs text-zinc-500 hover:text-zinc-100 transition-colors" data-testid="back-to-dashboard">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
            </Link>

            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div className="max-w-3xl">
                    <div className="overline mb-2">Listing ready</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">{product.title}</h1>
                </div>
                {product.images?.[0] && (
                    <div className="rounded-md border border-white/10 bg-surface p-1.5 w-28">
                        <img src={`data:image/jpeg;base64,${product.images[0]}`} alt={product.title} className="w-full aspect-square object-cover rounded" />
                    </div>
                )}
            </div>

            {/* Generic sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <Section title="SEO Title" icon={Tag} copyId="seo-title" copyText={r.seo_title}>
                    <p className="font-heading text-2xl font-semibold tracking-tight text-zinc-100" data-testid="seo-title">{r.seo_title}</p>
                </Section>
                <Section title="Meta Tags" icon={Hash} copyId="meta" copyText={`${r.meta_title}\n${r.meta_description}`}>
                    <div className="space-y-3">
                        <div><div className="overline mb-1">Title</div><p className="text-zinc-200" data-testid="meta-title">{r.meta_title}</p></div>
                        <div><div className="overline mb-1">Description</div><p className="text-sm text-zinc-400" data-testid="meta-description">{r.meta_description}</p></div>
                    </div>
                </Section>

                <Section title="Short Hook" icon={FileText} copyId="short-desc" copyText={r.short_description}>
                    <p className="text-lg leading-relaxed text-zinc-200" data-testid="short-desc">{r.short_description}</p>
                </Section>

                <Section title="Bullet Points" icon={List} copyId="bullets" copyText={(r.bullet_points || []).map((b) => `• ${b}`).join("\n")}>
                    <ul className="space-y-2.5" data-testid="bullets-list">
                        {(r.bullet_points || []).map((b, i) => (
                            <li key={`bullet-${i}-${b.slice(0, 30)}`} className="flex gap-3 text-sm text-zinc-300">
                                <span className="text-accent mt-0.5">▸</span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            </div>

            <div className="mb-4">
                <Section title="Long Description" icon={FileText} copyId="long-desc" copyText={r.long_description}>
                    <p className="leading-relaxed whitespace-pre-wrap text-zinc-300" data-testid="long-desc">{r.long_description}</p>
                </Section>
            </div>

            {(r.keywords || []).length > 0 && (
                <div className="mb-4">
                    <Section title="SEO Keywords" icon={Hash} copyId="keywords" copyText={(r.keywords || []).join(", ")}>
                        <div className="flex flex-wrap gap-2" data-testid="keywords-list">
                            {r.keywords.map((k, i) => (
                                <span key={`kw-${i}-${k}`} className="px-2.5 py-1 rounded-md bg-surface-2 border border-white/10 text-xs font-mono text-zinc-300">{k}</span>
                            ))}
                        </div>
                    </Section>
                </div>
            )}

            {(r.variant_descriptions || []).length > 0 && (
                <div className="mb-4">
                    <Section title="Variant Descriptions" copyId="variants" copyText={(r.variant_descriptions || []).map((v) => `${v.variant}: ${v.description}`).join("\n\n")}>
                        <div className="grid md:grid-cols-2 gap-3" data-testid="variants-list">
                            {r.variant_descriptions.map((v, i) => (
                                <div key={i} className="rounded-md border border-white/10 bg-surface-2 p-4">
                                    <div className="font-medium text-zinc-100 mb-1">{v.variant}</div>
                                    <p className="text-sm text-zinc-400">{v.description}</p>
                                </div>
                            ))}
                        </div>
                    </Section>
                </div>
            )}

            {/* Marketplace Tabs */}
            <div className="mt-10">
                <h2 className="font-heading text-2xl font-semibold tracking-tight mb-5">Marketplace formats</h2>
                <div className="flex gap-1 mb-0 border-b border-white/5">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-5 py-2.5 text-sm font-medium relative transition-colors ${
                                tab === t.id ? "text-accent" : "text-zinc-500 hover:text-zinc-200"
                            }`}
                            data-testid={`tab-${t.id}`}
                        >
                            {t.label}
                            {tab === t.id && <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-accent" />}
                        </button>
                    ))}
                </div>
                <div className="surface p-6 rounded-t-none border-t-0" data-testid={`tab-content-${tab}`}>
                    {Object.keys(mp).length === 0 ? (
                        <div className="text-zinc-500 font-mono text-sm">No marketplace-specific copy generated for {tab}.</div>
                    ) : (
                        <div className="space-y-6">
                            {mp.title && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="overline">Title</span>
                                        <CopyBtn text={mp.title} id={`${tab}-title`} />
                                    </div>
                                    <p className="font-heading text-xl font-semibold tracking-tight text-zinc-100">{mp.title}</p>
                                </div>
                            )}
                            {mp.bullets && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="overline">Bullets</span>
                                        <CopyBtn text={mp.bullets.map((b) => `• ${b}`).join("\n")} id={`${tab}-bullets`} />
                                    </div>
                                    <ul className="space-y-1.5">
                                        {mp.bullets.map((b, i) => <li key={`mpb-${i}-${b.slice(0, 30)}`} className="text-sm text-zinc-300"><span className="text-accent mr-2">▸</span>{b}</li>)}
                                    </ul>
                                </div>
                            )}
                            {mp.description && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="overline">Description</span>
                                        <CopyBtn text={mp.description} id={`${tab}-desc`} />
                                    </div>
                                    <p className="leading-relaxed whitespace-pre-wrap text-zinc-300">{mp.description}</p>
                                </div>
                            )}
                            {mp.tags && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="overline">Tags</span>
                                        <CopyBtn text={mp.tags.join(", ")} id={`${tab}-tags`} />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {mp.tags.map((t, i) => <span key={`mptag-${i}-${t}`} className="px-2.5 py-1 rounded-md bg-accent/5 border border-accent/20 text-xs font-mono text-accent">{t}</span>)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
